import { DetailedError } from 'tus-js-client'
import { ApiError } from '@/api/client'

/**
 * Why a transfer stopped, as a person can act on it.
 *
 * The error's own message is English and for a log — « tus: failed to upload chunk at offset 0 … »
 * — so each kind here has a sentence of its own under `assets.upload.failure`, and the home page's
 * import scene one under `home.onboarding.scenes.errors.uploadFailed.lines`.
 *
 * Two layers fail a transfer, and they are read apart: the API, which opens the session or takes a
 * small file in one request and answers an `ErrorBody` whose `code` names the refusal, and the
 * upload server, which speaks tus and answers a bare status.
 */
export type UploadFailureKind =
  /** No answer at all: the network, a proxy, the laptop going to sleep. */
  | 'network'
  /**
   * The upload server and this tab disagree on where the transfer stands — the offset (409), a
   * lock (423), a chunk's checksum (460). Resuming asks the server for its offset and goes on.
   */
  | 'interrupted'
  /** The file is over what the server takes. */
  | 'tooLarge'
  /** The server refused the file or what was said about it. */
  | 'refused'
  /** The caller may not add a file there any more. */
  | 'forbidden'
  /** The workspace, the folder or the media the file was going into no longer exists. */
  | 'destinationGone'
  /** Too many requests at once: the same request goes through a moment later. */
  | 'rateLimited'
  /**
   * The organization's credit is spent, or does not cover the file's first month of storage, which
   * an upload pays when it lands: refused at the opening, before a byte is sent, or at the last
   * chunk, which ends the session and keeps nothing.
   */
  | 'creditsExhausted'
  /** A spending limit set on the organization stops the import. */
  | 'budgetExceeded'
  /**
   * The upload's session is gone — terminated, expired, or no longer this tab's to send to. Nothing
   * is left to resume: a retry opens a new session and sends the file from its first byte.
   */
  | 'session'
  /** The server stopped waiting before the request was over: a slow link, or a busy server. */
  | 'timedOut'
  /** The server failed, or the two ends disagree on the protocol: nothing on the caller's side. */
  | 'server'

/** The kind an API refusal says by its code, which outranks its status. */
const KIND_OF_CODE: Partial<Record<NonNullable<ApiError['code']>, UploadFailureKind>> = {
  payload_too_large: 'tooLarge',
  unsupported_media_type: 'refused',
  validation: 'refused',
  forbidden: 'forbidden',
  unauthorized: 'forbidden',
  not_signed_in: 'forbidden',
  no_organization: 'forbidden',
  second_factor_required: 'forbidden',
  not_found: 'destinationGone',
  rate_limited: 'rateLimited',
  credits_exhausted: 'creditsExhausted',
  budget_exceeded: 'budgetExceeded',
  timed_out: 'timedOut',
}

/**
 * The kind of an API answer whose code says nothing more, or that carries none — a proxy's page in
 * front of the API. The refusals the API words itself (a missing workspace, a refused name) carry
 * their code, so a bare status says what the edge saw and no more.
 */
function kindOfApiStatus(status: number): UploadFailureKind {
  if (status === 413) return 'tooLarge'
  if (status === 401 || status === 403) return 'forbidden'
  if (status === 429) return 'rateLimited'
  if (status === 408 || status === 504) return 'timedOut'
  return 'server'
}

/** The kind of the upload server's answer to a tus request. */
function kindOfTusStatus(status: number): UploadFailureKind {
  // An upload that was terminated or expired, or a token that is not this upload's: the session
  // cannot be sent to any more.
  if (status === 404 || status === 410 || status === 401 || status === 403) return 'session'
  if (status === 409 || status === 423 || status === 460) return 'interrupted'
  // The last chunk completes the upload, which pays the file's first month: the credit did not
  // cover it, and the session is over.
  if (status === 402) return 'creditsExhausted'
  if (status === 413) return 'tooLarge'
  if (status === 429) return 'rateLimited'
  if (status === 408 || status === 504) return 'timedOut'
  // Anything else — a 5xx, or a 4xx about the request's shape (a header, a chunk's size) that this
  // client and the upload server should never disagree on — is ours to fix, not the person's.
  return 'server'
}

/** Classify a transfer's failure, from whichever layer raised it. */
export function uploadFailureKind(error: unknown): UploadFailureKind {
  if (error instanceof ApiError) {
    return (error.code && KIND_OF_CODE[error.code]) || kindOfApiStatus(error.status)
  }
  if (error instanceof DetailedError) {
    const status = error.originalResponse?.getStatus()
    return status ? kindOfTusStatus(status) : 'network'
  }
  return 'network'
}

/**
 * The failures a retry cannot fix and another file can: the server refused this one. Resending the
 * same bytes would be refused the same way.
 */
export const NEW_FILE_KINDS: ReadonlySet<UploadFailureKind> = new Set(['tooLarge', 'refused'])

/**
 * The failures after which the server holds no session to resume: it is gone, or the completion
 * was refused for want of credit and ended it. A retry sends the file into a new session from its
 * first byte.
 */
export const RESTART_KINDS: ReadonlySet<UploadFailureKind> = new Set(['session', 'creditsExhausted'])
